import { Button, Checkbox, Input, Modal, Select, Switch, Tooltip } from "antd";
import React, { useState } from "react";
import { svg } from "../../../../../Assets/svg";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  generateAdditionalProductFieldsOptions,
  generatePriceCalculationOptions,
  PriceCalculationEnum,
} from "../../../Products/productsTypes";
import { useAppDispatch } from "../../../../../store";
import {
  createProductTypeAPI,
  updateProductTypeAPI,
} from "../../productTypeApis";
import { InfoCircleOutlined } from "@ant-design/icons";

interface Props {
  open: boolean;
  onClose: () => void;
  refreshData: () => void;
  editData?: any | null;
}

const CreateProductTypeModal = ({
  open,
  onClose,
  refreshData,
  editData,
}: Props) => {
  const dispatch = useAppDispatch();
  const [saving, setSaving] = useState(false);
  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: editData?.name ?? null,
      load_key: editData?.load_key ?? null,
      mark_up: editData?.mark_up ?? 0,
      installation_mark_up: editData?.installation_mark_up ?? 0,
      quote_product_is_required: editData?.quote_product_is_required ?? false,
      price_calculation:
        editData?.price_calculation === true
          ? PriceCalculationEnum.CALCULATION
          : editData?.price_calculation === false
          ? PriceCalculationEnum.NOT_SET
          : (editData?.price_calculation ?? PriceCalculationEnum.NOT_SET),
      is_regenrate: false,
    },
    validationSchema: Yup.object({
      name: Yup.string().required("Product Type is required"),
      mark_up: Yup.number()
        .min(0, "Mark Up must be greater than or equal to 0")
        .max(100, "Mark Up must be less than or equal to 100")
        .required("Mark Up is required"),
      installation_mark_up: Yup.number()
        .min(0, "Installation Mark Up must be greater than or equal to 0")
        .max(100, "Installation Mark Up must be less than or equal to 100")
        .required("Installation Mark Up is required"),
    }),
    onSubmit: (values) => {
      const payload = {
        ...(editData?._id && { id: editData?._id }),
        name: values?.name,
        ...(values?.load_key && { load_key: values?.load_key }),
        mark_up: values?.mark_up || 0,
        installation_mark_up: values?.installation_mark_up || 0,
        quote_product_is_required: values?.quote_product_is_required || false,
        price_calculation: values?.price_calculation || PriceCalculationEnum.NOT_SET,
        is_regenrate: values?.is_regenrate || false,
      };

      setSaving(true);

      if (editData?._id) {
        dispatch(
          updateProductTypeAPI({
            payload,
            onSuccess: () => {
              setSaving(false);
              refreshData();
              onClose();
            },
            onError: () => {
              setSaving(false);
            },
          }),
        );
      } else {
        dispatch(
          createProductTypeAPI({
            payload,
            onSuccess: () => {
              setSaving(false);
              refreshData();
              onClose();
            },
            onError: () => {
              setSaving(false);
            },
          }),
        );
      }
    },
  });
  return (
    <Modal
      open={open}
      centered
      width={"36.75rem"}
      footer={null}
      onCancel={onClose}
      closeIcon={svg?.closeIcon}
      title={
        <div className="text-center fs-4 fw-bolder lh-m">
          {editData ? "Update Product Type" : "Create Product Type"}
        </div>
      }
      className="commonModalClass"
    >
      {!editData && (
        <div className="text-center fs-6 fw-normal">
          New types will not be included in price calculations automatically.
          Please contact the developer for configuration
        </div>
      )}
      <form
        className="page-form d-flex flex-column gap-3"
        onSubmit={formik.handleSubmit}
      >
        <div className="d-flex flex-column gap-3">
          <div>
            <label className="custom-label mb-1 ff">Product Type</label>
            <Input
              name="name"
              placeholder="Product Type"
              value={formik?.values?.name}
              onChange={formik.handleChange}
              className="form-control form-control-lg"
            />
            {formik.errors.name && formik.touched.name && (
              <span className="text-danger">
                {formik.errors.name as React.ReactNode}
              </span>
            )}
          </div>
          <div className="row">
            <div className="col-6">
              <label className="custom-label mb-1 ff">Additional Field</label>
              <Select
                value={formik?.values?.load_key}
                onChange={(e) => formik.setFieldValue("load_key", e)}
                suffixIcon={svg?.selectDownArrow}
                options={generateAdditionalProductFieldsOptions()}
                placeholder="Select Additional Field"
                allowClear
              />
            </div>
            <div className="col-6">
              <label className="custom-label mb-1 ff">Mark Up</label>
              <Input
                name="mark_up"
                placeholder="0"
                type="number"
                value={formik?.values?.mark_up}
                onChange={formik.handleChange}
                prefix={<span>%</span>}
                className="form-control form-control-lg"
              />
              {formik.errors.mark_up && formik.touched.mark_up && (
                <span className="text-danger">
                  {formik.errors.mark_up as React.ReactNode}
                </span>
              )}
            </div>
          </div>
          <div className="row">
            <div className="col-6">
              <label className="custom-label mb-1 ff">Installation Mark Up</label>
              <Input
                name="installation_mark_up"
                placeholder="0"
                type="number"
                value={formik?.values?.installation_mark_up}
                onChange={formik.handleChange}
                prefix={<span>%</span>}
                className="form-control form-control-lg"
              />
              {formik.errors.installation_mark_up &&
                formik.touched.installation_mark_up && (
                  <span className="text-danger">
                    {formik.errors.installation_mark_up as React.ReactNode}
                  </span>
                )}
            </div>
            <div className="col-6">
              <label className="custom-label mb-1 ff">Price Calculation</label>
              <Select
                value={formik?.values?.price_calculation}
                onChange={(e) => formik.setFieldValue("price_calculation", e)}
                suffixIcon={svg?.selectDownArrow}
                options={generatePriceCalculationOptions()}
                placeholder="Select Price Calculation"
              />
            </div>
          </div>
          <div className="d-flex align-items-center gap-4">
            <div className="d-flex align-items-center gap-1">
              <label className="custom-label mb-1 ff">Mark as Necessary?</label>
              <Tooltip
                title={`If this product type is marked as necessary, a warning message will appear in the quotation when no product of this type is added. Users can still ignore the warning and continue.`}
              >
                <InfoCircleOutlined />
              </Tooltip>
            </div>
            <Switch
              className="customSwitch"
              checkedChildren="Yes"
              unCheckedChildren="No"
              checked={formik?.values?.quote_product_is_required}
              onChange={(e) =>
                formik.setFieldValue("quote_product_is_required", e)
              }
            />
          </div>
          {editData?._id ? (
            <>
            <div className="col-12">
              <Checkbox
                checked={formik?.values?.is_regenrate}
                onChange={(e) =>
                  formik.setFieldValue("is_regenrate", e.target.checked)
                }
                className="custom-checkbox align-self-start"
              >
                <span className="fs-7">
                  Recalculate prices for all product variants in this product
                  type.
                </span>
              </Checkbox>
            </div>
              <div>
                <span className="text-primary">
                 Notes : <small>
                    If you do not select the{" "}
                    <strong>"Recalculate Prices"</strong> checkbox above, you
                    will need to go to the <strong>Price Sheet</strong> module
                    later and click <strong>"Regenerate Price"</strong> from the
                    three-dot menu of the default price sheet to recalculate the
                    prices of the product variants.
                  </small>
                </span>
              </div>

            </>
          ) : (
            ""
          )}
          <div className="d-flex justify-content-center gap-3 mt-4">
            <Button
              onClick={onClose}
              disabled={saving}
              className="btn btn-lg btn-outline-secondary h-auto px-5"
            >
              CLOSE
            </Button>

            <Button
              loading={saving}
              htmlType="submit"
              className="btn btn-lg btn-primary h-auto px-5"
            >
              {editData ? "UPDATE" : "SAVE"}
            </Button>
          </div>
        </div>
      </form>
    </Modal>
  );
};

export default CreateProductTypeModal;
